import { Signal } from "./Signal";

export interface LoopFrame {
  dt: number;
  elapsed: number;
}

export class Loop {
  readonly preUpdate = new Signal<LoopFrame>();
  readonly update = new Signal<LoopFrame>();
  readonly render = new Signal<LoopFrame>();

  private rafId: number | null = null;
  private lastTime = 0;
  private elapsed = 0;
  private running = false;
  private readonly maxDt: number;

  constructor(maxDt = 0.05) {
    this.maxDt = maxDt;
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    this.lastTime = performance.now();
    this.scheduleNext();
  }

  stop(): void {
    this.running = false;
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }

  pause(): void {
    if (!this.running) return;
    this.stop();
  }

  resume(): void {
    if (this.running) return;
    this.start();
  }

  get isRunning(): boolean {
    return this.running;
  }

  private scheduleNext(): void {
    this.rafId = requestAnimationFrame(this.tick);
  }

  private tick = (now: number): void => {
    if (!this.running) return;
    const rawDt = (now - this.lastTime) / 1000;
    this.lastTime = now;
    const dt = Math.min(Math.max(rawDt, 0), this.maxDt);
    this.elapsed += dt;
    const frame: LoopFrame = { dt, elapsed: this.elapsed };
    this.preUpdate.emit(frame);
    this.update.emit(frame);
    this.render.emit(frame);
    this.scheduleNext();
  };
}
